/**
 * Core services cards — scroll-scrub animation.
 *
 * Mirrors the "Breakthrough Energy" feel of smart-video-scroll-reveal.js:
 *   • Each card scrubs up into view from below (fade + slight y + scale).
 *   • It dwells in its rest state while fully in view.
 *   • As it exits the top of the viewport, the media shrinks (scale + bigger
 *     border-radius) and the overlay panel fades up — so the previous card
 *     visibly "shrinks" as you scroll onto the next one.
 *
 * A single scrub timeline per card (start: "top bottom", end: "bottom top")
 * owns all keyframes for that card, which guarantees a perfectly reversible
 * animation when scrolling back up.
 *
 * Cards are populated asynchronously by partials/custom-posts.hbs → the
 * `core-cards:ready` CustomEvent (plus DOMContentLoaded / window.load
 * fallbacks) is used to attach ScrollTrigger safely after the HTML is
 * injected. An `initialised` guard prevents double-init when this file is
 * both loaded standalone and bundled into casper.js via Gulp.
 *
 * No-op if the page has no `.core-cards-grid` or if GSAP/ScrollTrigger
 * aren't loaded — this file is safe to include on every page.
 */
(function () {
  "use strict";

  var initialised = false;

  function hasGsap() {
    return (
      typeof window.gsap !== "undefined" &&
      typeof window.ScrollTrigger !== "undefined"
    );
  }

  function getCards() {
    return Array.prototype.slice.call(
      document.querySelectorAll(".core-cards-grid .core-card")
    );
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function setup() {
    if (initialised) return;
    var cards = getCards();
    if (!cards.length) return;
    if (!hasGsap()) return;

    initialised = true;

    gsap.registerPlugin(ScrollTrigger);

    /* Reduced motion: leave the cards in their CSS rest state and bail */
    if (prefersReducedMotion()) {
      cards.forEach(function (card) {
        var media = card.querySelector(".core-card-media");
        var overlay = card.querySelector(".core-card-overlay");
        if (media) gsap.set(media, { clearProps: "all" });
        if (overlay) gsap.set(overlay, { clearProps: "all" });
      });
      return;
    }

    var EASE = "none";

    /* ================================================================
     *  SECTION HEADING REVEAL
     *  Only animate the heading (NOT the cards' container) so per-card
     *  transforms don't get stacked on top of a parent transform.
     * ================================================================ */
    var heading = document.querySelector(".core-card-main .core-heading");
    if (heading) {
      gsap.fromTo(
        heading,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: EASE,
          scrollTrigger: {
            trigger: ".core-card-main",
            start: "top 92%",
            end: "top 65%",
            scrub: 0.75,
            invalidateOnRefresh: true,
          },
        }
      );
    }

    /* Phase markers inside the per-card timeline (all durations are relative) */
    var ENTER_START = 0.0;
    var ENTER_DUR = 0.5; /* 0.0 → 0.5  : rise up into view from below */
    var DWELL_END = 0.62; /* 0.5 → 0.62 : at rest (feels grounded) */
    var EXIT_START = DWELL_END;
    var EXIT_DUR = 1.0 - EXIT_START; /* 0.62 → 1.0 : shrink out the top */

    cards.forEach(function (card, index) {
      var media = card.querySelector(".core-card-media");
      var overlay = card.querySelector(".core-card-overlay");
      if (!media) return;

      /* Stack later cards on top so earlier ones visually recede behind */
      card.style.zIndex = String(10 + index);
      card.style.willChange = "transform";
      media.style.willChange = "transform, border-radius, opacity";
      if (overlay) overlay.style.willChange = "transform, opacity";

      gsap.set(media, { transformOrigin: "50% 50%" });
      if (overlay) gsap.set(overlay, { transformOrigin: "50% 100%" });

      /* -------- Single scrub timeline covers the full card journey -------- */
      var tl = gsap.timeline({
        defaults: { ease: EASE },
        scrollTrigger: {
          trigger: card,
          /* Full journey: card top enters from viewport bottom →
             card bottom leaves from viewport top. */
          start: "top bottom",
          end: "bottom top",
          scrub: 0.75,
          invalidateOnRefresh: true,
          fastScrollEnd: false,
        },
      });

      /* ====== ENTER PHASE — card rises from below into view ====== */
      tl.fromTo(
        media,
        { scale: 0.9, y: 100, opacity: 0.3 },
        {
          scale: 1,
          y: 0,
          opacity: 1,
          duration: ENTER_DUR,
        },
        ENTER_START
      );

      if (overlay) {
        tl.fromTo(
          overlay,
          { y: 60, opacity: 0, scale: 0.96 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: ENTER_DUR,
          },
          ENTER_START + 0.08
        );
      }

      /* ====== DWELL (0.4 → 0.6) — card sits at its rest state ====== */
      /* No tweens here: a "hold" by design, makes scroll feel grounded. */

      /* ====== EXIT PHASE (0.6 → 1.0) ====== */
      tl.to(
        media,
        {
          scale: 0.92,
          y: -40,
          opacity: 0.5,
          duration: EXIT_DUR,
        },
        EXIT_START
      );

      if (overlay) {
        tl.to(
          overlay,
          {
            y: -30,
            opacity: 0,
            scale: 0.96,
            duration: EXIT_DUR - 0.05,
          },
          EXIT_START + 0.05
        );
      }
    });

    /* Recompute trigger positions after feature images finish loading */
    var imgs = document.querySelectorAll(".core-cards-grid .core-card-image");
    if (imgs.length) {
      var pending = imgs.length;
      function done() {
        if (--pending === 0) ScrollTrigger.refresh();
      }
      Array.prototype.forEach.call(imgs, function (img) {
        if (img.complete) {
          done();
        } else {
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        }
      });
    }

    ScrollTrigger.refresh();
  }

  /* Primary entry: fetchCoreCards fires this once cards are in the DOM */
  document.addEventListener("core-cards:ready", function () {
    if (initialised) {
      /* Cards were re-rendered (unlikely but safe) — just refresh positions */
      if (hasGsap()) ScrollTrigger.refresh();
      return;
    }
    setup();
  });

  /* Fallback: cards might already be in the DOM (e.g. server-rendered) */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      if (getCards().length) setup();
    });
  } else if (getCards().length) {
    setup();
  }

  /* Final safety net: GSAP may load after this file executed */
  window.addEventListener(
    "load",
    function () {
      if (!initialised && getCards().length && hasGsap()) setup();
      if (initialised) ScrollTrigger.refresh();
    },
    { once: true }
  );
})();
